<template>
  <el-tooltip popper-class="notifications-popper tw:p-4!" effect="dark">
    <template #content>
      <div v-if="sortedRecipes?.length" class="tw:flex tw:flex-col tw:gap-2">
        <MobileNotificationItem
          v-for="recipe in sortedRecipes"
          :key="`${recipe?.craftStartTime}`"
          v-model="isLoadingClaimButton"
          :recipe="recipe"
          :icon="'123'"
        />
      </div>
      <div v-else class="no-notifications">{{ $t('coreNotificationsEmpty') }}</div>
    </template>
  </el-tooltip>
</template>

<script setup lang="ts">
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { WebSocketsKeys } from '~/types/common';
import type { UnclaimedRecipeProps } from '~/types/crafting';

const store = useMainStore();
const { address } = useWeb3ModalAccount();

const { $websockets: websockets } = useNuxtApp();
const craftingSocket = websockets?.[WebSocketsKeys.craftingActivity];

const sortedRecipes = computed(() => {
  const allRecipes: UnclaimedRecipeProps[] = [];

  store.cachedClaimItems.forEach((claimItem) => {
    Object.values(claimItem.unclaimedByRecipeIndexes).forEach((modifierData) => {
      // Find the corresponding recipe in crafting socket by userRecipeIndex
      const socketRecipe = craftingSocket?.parsedData?.find((recipe: any) =>
        BigNumber(recipe.userRecipeIndex).eq(modifierData.userRecipeIndex)
      );

      allRecipes.push({
        craftDuration: modifierData.recipe.craftDuration,
        craftStartTime: socketRecipe?.craftStartTime || new Date().toISOString(),
        craftEndTime: socketRecipe?.craftEndTime || new Date().toISOString(),
        products: [...modifierData.recipe.products],
        recipeId: Number(modifierData.recipe.recipeId),
        multiplyModifier: modifierData.multiplyModifier,
        unclaimedModifier: modifierData.unclaimedModifier,
        userRecipeIndex: modifierData.userRecipeIndex,
        buildingHash: modifierData.buildingHash
      });
    });
  });

  // Add running recipes from socket that aren't in cachedClaimItems yet
  const existingRecipeIds = new Set(allRecipes.map((item) => item.userRecipeIndex));
  const socketRecipes: UnclaimedRecipeProps[] = craftingSocket?.parsedData || [];

  const newSocketRecipes = socketRecipes.filter((socketItem) => !existingRecipeIds.has(socketItem.userRecipeIndex));

  newSocketRecipes.forEach((socketItem) => {
    allRecipes.push({
      ...socketItem
    });
  });

  return allRecipes.sort(
    (a: UnclaimedRecipeProps, b: UnclaimedRecipeProps) =>
      Number(new Date(a?.craftStartTime)) - Number(new Date(b.craftStartTime))
  );
});

const isLoadingClaimButton = ref(false);

watch([() => store.notificationUpdateVersion, address], async ([newUpdateVersion]) => {
  if (!BigNumber(newUpdateVersion).isNaN()) {
    await refreshNuxtData('unclaimed-recipes');
  }
});
</script>

<style lang="scss" scoped>
.available-claim {
  margin-right: 10px;
}
</style>

<style lang="scss">
.notifications-popper {
  z-index: 5;
  background: rgba(0, 0, 0, 0.75) !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%);
  width: 96%;
  color: white;
  text-align: center;
  border-radius: 10px;
  max-height: calc(100vh - 80px - 68px);
  overflow: auto;
  backdrop-filter: blur(4px);
}

.el-popper__arrow {
  display: none;
}

.no-notifications {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
}
</style>
